import { Button, Col, Flex, Row } from '@wyz/ui';
import { ReactNode, type HTMLAttributes } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans } from '~/common/context';
import { OptionSelector } from '~/purchase/components';
import { useWcwCatalog } from '~/purchase/api';
import { WcwSearchHelpers } from '~/purchase/helpers';
import { IWcwSearchFormSchema } from '~/purchase/schemas';

function WcwSearchTrigger({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

interface LayoutProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

function Layout({ children, className = '', ...props }: LayoutProps) {
  return (
    <div className={`search-bar-trigger row mt-3 ${className}`} {...props}>
      {children}
    </div>
  );
}

function Body({ customerId }: { customerId: string }) {
  const methods = useFormContext<IWcwSearchFormSchema>();
  const values = methods.watch();
  const canSearch = WcwSearchHelpers.canSubmitWcwSearch(values);

  const wcwCatalog = useWcwCatalog({
    getSupplierAvailability: {
      enabled: true,
      args: { customerId },
    },
  });

  const availableSets = [
    ...new Set(
      [
        ...(wcwCatalog.getSupplierAvailability.data?.data.flatMap(
          (s) => s.availableSalesUnits,
        ) ?? []),
      ].sort((a, b) => a - b),
    ),
  ];

  return (
    <Flex alignItems='center' justifyContent='end'>
      <Row className='no-label-margin'>
        <Col>
          <OptionSelector
            selected={values.set ?? null}
            onChange={(newValue) => {
              methods.setValue('set', newValue, { shouldValidate: true });
            }}
          >
            {availableSets.map((salesUnit) => (
              <OptionSelector.Item key={salesUnit} value={salesUnit}>
                <div className='no-wrap'>
                  <Trans
                    i18nKey='app.purchase.wcw.search_page.set_option'
                    count={salesUnit}
                  />
                </div>
              </OptionSelector.Item>
            ))}
          </OptionSelector>
        </Col>
        <Col>
          <Button
            type='submit'
            id='search'
            form='wcw-search-form'
            variant='primary'
            disabled={!canSearch}
            size='sm'
          >
            <Trans i18nKey='app.common.buttons.search' />
          </Button>
        </Col>
      </Row>
    </Flex>
  );
}

function ErrorSection() {
  return null;
}

const WcwSearchTriggerBar = Object.assign(WcwSearchTrigger, {
  Layout,
  Body,
  ErrorSection,
});

export default WcwSearchTriggerBar;
